/**
 * VerifyingIndicator Component
 *
 * Subtle, non-intrusive indicator shown during invisible CAPTCHA verification.
 * Positioned in bottom-right corner, doesn't block user interaction.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import React from 'react';
import { createPortal } from 'react-dom';
import type { VerifyingIndicatorProps } from './types';
import {
  CAPTCHA_Z_INDEX,
  VERIFYING_INDICATOR_POSITION,
} from './constants';

/**
 * VerifyingIndicator Component
 *
 * Shows a subtle "Verifying..." message during invisible CAPTCHA execution.
 * Uses portal to render outside of parent DOM hierarchy.
 *
 * @param visible - Whether to show the indicator
 * @param message - Custom message (default: "Verifying...")
 */
export const VerifyingIndicator: React.FC<VerifyingIndicatorProps> = ({
  visible = true,
  message = 'Verifying...',
}) => {
  // Don't render if not visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <div
      className="fixed flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-lg transition-all duration-200"
      style={{
        bottom: VERIFYING_INDICATOR_POSITION.bottom,
        right: VERIFYING_INDICATOR_POSITION.right,
        zIndex: CAPTCHA_Z_INDEX.INDICATOR,
        animation: 'fadeInUp 0.2s ease-out',
      }}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      {/* Spinner */}
      <svg
        className="h-4 w-4 animate-spin text-emerald-600"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>

      {/* Message */}
      <span className="text-sm font-medium text-gray-600">{message}</span>

      {/* Inline styles for animation (in case Tailwind doesn't have it) */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default VerifyingIndicator;
